import { memo } from 'react';
import type { BoardRow as Row } from '@shared/types';
import { AttentionFlags, AttentionScore } from '@web/components/Attention';
import { ReasonChip } from '@web/components/ReasonChip';
import { StateChip } from '@web/components/StateChip';
import { TemperatureMeter } from '@web/components/TemperatureMeter';
import { IdleTimer } from '@web/components/Timers';
import { cn } from '@web/lib/cn';

/**
 * One live conversation, as a table row.
 *
 * Fixed height and fixed-width numeric columns: a ticking timer that changes
 * glyph width makes every row to its right shimmer once a second. `contain`
 * turns each row into a paint island so a timer cell cannot invalidate its
 * neighbours.
 *
 * `viewTransitionName` lets the browser FLIP this row into its new position on
 * the rare occasions the board re-ranks — nothing else here animates.
 */

/** Names the row so the browser can FLIP it when the board re-ranks. */
function transitionName(id: string): React.CSSProperties {
  return { viewTransitionName: `row-${id}` };
}

export interface BoardRowProps {
  row: Row;
  selected: boolean;
  isNew: boolean;
  onOpen: (conversationId: string) => void;
}

function label(row: Row): string {
  const parts = [
    row.candidateName,
    row.trade ?? 'trade unknown',
    row.handlingState,
    `attention ${String(Math.round(row.attentionScore))}`,
    `${row.temperature} lead`,
  ];
  return `Open ${parts.join(', ')}`;
}

export const BoardTableRow = memo(function BoardTableRow({
  row,
  selected,
  isNew,
  onOpen,
}: BoardRowProps) {
  return (
    <tr
      style={transitionName(row.conversationId)}
      onClick={() => {
        onOpen(row.conversationId);
      }}
      className={cn(
        'h-9 cursor-default border-b border-rule [contain:layout_paint_style]',
        'hover:bg-hover',
        selected && 'bg-hover',
        isNew && 'row-arrival',
      )}
    >
      <td className="w-[86px] px-2">
        <StateChip state={row.handlingState} />
      </td>

      <td className="max-w-[220px] min-w-[160px] px-2">
        {/*
          The row is clickable for the pointer; this button is what a keyboard
          and a screen reader use. One button per row rather than a roving
          tabindex grid — at sixty rows the Tab order is the simpler contract.
        */}
        <button
          type="button"
          className="flex w-full items-baseline gap-2 text-left focus-visible:outline-2"
          aria-label={label(row)}
          onClick={(event) => {
            event.stopPropagation();
            onOpen(row.conversationId);
          }}
        >
          <span className="truncate font-medium text-ink">{row.candidateName}</span>
          <span className="truncate text-[11px] text-ink-faint">{row.trade ?? '—'}</span>
        </button>
      </td>

      <td className="px-2">
        <div className="flex items-center gap-2">
          {row.handlingState === 'parked' && <ReasonChip reason={row.parkReason} />}
          <span className="line-clamp-1 text-ink-dim">{row.lastMessagePreview ?? '—'}</span>
        </div>
      </td>

      <td className="w-[64px] px-2 text-right">
        <IdleTimer since={row.lastMessageAt} />
      </td>

      <td className="w-[40px] px-2">
        <TemperatureMeter temperature={row.temperature} />
      </td>

      <td className="w-[56px] px-2">
        <AttentionScore score={row.attentionScore} />
      </td>

      <td className="w-[150px] px-2">
        <AttentionFlags flags={row.attentionFlags} />
      </td>

      <td className="w-[96px] px-2">
        <span className="truncate font-mono text-[11px] text-ink-faint">
          {row.assignee?.name ?? '—'}
        </span>
      </td>
    </tr>
  );
});

/**
 * The same row on a phone.
 *
 * A separate component on purpose. A dense eight-column table cannot be made
 * usable at 375px by any amount of responsive cleverness, and a single
 * "ConversationItem" with `variant` props would be a 300-line chameleon serving
 * neither surface. Two ~80-line components that share the leaf vocabulary is
 * the cheaper answer.
 */
export const BoardCard = memo(function BoardCard({ row, isNew, onOpen }: BoardRowProps) {
  return (
    <button
      type="button"
      style={transitionName(row.conversationId)}
      onClick={() => {
        onOpen(row.conversationId);
      }}
      aria-label={label(row)}
      className={cn(
        'flex min-h-14 w-full flex-col gap-1 border-b border-rule px-3 py-2 text-left',
        'active:bg-hover',
        isNew && 'row-arrival',
      )}
    >
      <div className="flex items-center gap-2">
        <StateChip state={row.handlingState} />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{row.candidateName}</span>
        <TemperatureMeter temperature={row.temperature} />
        <IdleTimer since={row.lastMessageAt} />
      </div>
      <div className="flex items-center gap-2">
        <span className="truncate text-[11px] text-ink-faint">{row.trade ?? '—'}</span>
        <span className="min-w-0 flex-1 truncate text-xs text-ink-dim">
          {row.lastMessagePreview ?? '—'}
        </span>
      </div>
      {row.attentionFlags.length > 0 && <AttentionFlags flags={row.attentionFlags} limit={4} />}
    </button>
  );
});
